<script>
import { defineAsyncComponent, ref, computed, watch } from 'vue';
import { useStore } from 'vuex';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useWindowSize } from '@vueuse/core';

import wootConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components-next/call/FloatingCallWidget.vue')
);

import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import { useCallsStore } from 'dashboard/stores/calls';
import { useCallSession } from 'dashboard/composables/useCallSession';
import FreeswitchVoiceClient from 'dashboard/api/channel/voice/freeswitchVoiceClient';

export default {
  components: {
    NextSidebar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    CopilotLauncher,
    CopilotContainer,
    FloatingCallWidget,
    MobileSidebarLauncher,
  },
  setup() {
    const store = useStore();
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();
    const { width: windowWidth } = useWindowSize();
    const callsStore = useCallsStore();

    useCallSession();

    const currentUserId = computed(() => store?.getters?.getCurrentUserID);
    watch(
      currentUserId,
      (userId) => {
        if (userId) {
          FreeswitchVoiceClient.initialize();
        }
      },
      { immediate: true }
    );

    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
      windowWidth,
      hasActiveCall: computed(() => callsStore.hasActiveCall),
      hasIncomingCall: computed(() => callsStore.hasIncomingCall),
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
    };
  },
  computed: {
    isSettingsRoute() {
      const path = this.$route.path;
      if (path.includes('/portals/') && path.includes('/settings')) {
        return false;
      }
      if (path.includes('/captain/') && path.includes('/settings')) {
        return false;
      }
      if (path.includes('/settings/captain')) {
        return false;
      }
      return path.includes('/settings');
    },
    hasCaptain() {
      return this.$store.getters['accounts/isFeatureEnabledonAccount'](
        this.accountId,
        'captain'
      );
    },
    hasAdvancedAssignment() {
      return this.$store.getters['accounts/isFeatureEnabledonAccount'](
        this.accountId,
        'advanced_assignment'
      );
    },
    settingsMenuItems() {
      const accountScopedRoute = (name, params = {}, query = {}) => {
        return { name, params: { accountId: this.accountId, ...params }, query };
      };
      
      return [
        {
          name: 'Account Settings',
          label: this.$t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
        },
        {
          name: 'Agents',
          label: this.$t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Teams',
          label: this.$t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          to: accountScopedRoute('settings_teams_list'),
        },
        ...(this.hasAdvancedAssignment
          ? [
              {
                name: 'Agent Assignment',
                label: this.$t('SIDEBAR.AGENT_ASSIGNMENT'),
                icon: 'i-lucide-user-cog',
                to: accountScopedRoute('assignment_policy_index'),
              },
            ]
          : []),
        {
          name: 'Inboxes',
          label: this.$t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Labels',
          label: this.$t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Custom Attributes',
          label: this.$t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Automation',
          label: this.$t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-repeat',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Macros',
          label: this.$t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Canned Responses',
          label: this.$t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'WhatsApp Templates',
          label: 'Templates',
          icon: 'i-lucide-layout-template',
          to: accountScopedRoute('whatsapp_templates_list'),
        },
        {
          name: 'Integrations',
          label: this.$t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Audit Logs',
          label: this.$t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Custom Roles',
          label: this.$t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        {
          name: 'SLA',
          label: this.$t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Conversation Workflow',
          label: this.$t('SIDEBAR.CONVERSATION_WORKFLOW'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('conversation_workflow_index'),
        },
        {
          name: 'Security',
          label: this.$t('SIDEBAR.SECURITY'),
          icon: 'i-lucide-shield',
          to: accountScopedRoute('security_settings_index'),
        },
        {
          name: 'Billing',
          label: this.$t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
        },
      ];
    },
    isSmallScreen() {
      return this.windowWidth < wootConstants.SMALL_SCREEN_BREAKPOINT;
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'general_settings_index',
        'agent_list',
      ].includes(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
  },
  watch: {
    isSmallScreen: {
      handler() {
        const { LAYOUT_TYPES } = wootConstants;
        if (window.innerWidth <= wootConstants.SMALL_SCREEN_BREAKPOINT) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
    closeSettings() {
      this.$router.push({ name: 'home', params: { accountId: this.accountId } });
    },
  },
};
</script>

<template>
  <div class="flex flex-grow h-screen overflow-hidden text-n-slate-12">
    <NextSidebar
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />

    <main
      class="relative flex flex-1 h-full w-full min-h-0 px-0 overflow-hidden bg-n-surface-1 dark:bg-[#101011]"
    >
      <UpgradePage
        v-show="showUpgradePage"
        ref="upgradePageRef"
        :bypass-upgrade-page="bypassUpgradePage"
      >
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
      </UpgradePage>
      <template v-if="!showUpgradePage">
        <router-view v-if="!isSettingsRoute" />
        <woot-modal
          v-else
          :show="true"
          :on-close="closeSettings"
          modal-type="centered"
          size="!w-[85vw] !max-w-[1200px]"
        >
          <div class="flex h-[85vh] w-full max-w-[1200px] bg-n-surface-1 dark:bg-[#101011] rounded-xl overflow-hidden text-[13px]">
            <!-- Settings Sidebar inside the popup -->
            <div class="w-64 border-r border-n-weak bg-n-background dark:bg-black flex flex-col pl-4 pt-4 pb-4 shrink-0">
              <span class="text-sm font-medium px-3 mb-4 text-n-slate-12">Settings</span>
              <nav class="flex-grow overflow-y-auto flex flex-col gap-1 pr-2">
                <router-link
                  v-for="item in settingsMenuItems"
                  :key="item.name"
                  :to="item.to"
                  class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3"
                  active-class="bg-n-alpha-1 dark:bg-n-alpha-3 text-n-slate-12"
                >
                  <span v-if="item.icon" :class="[item.icon, 'size-[14px] shrink-0']" />
                  <span class="truncate">{{ item.label }}</span>
                </router-link>
              </nav>
            </div>

            <!-- Settings Content Area inside the popup -->
            <div class="flex-grow overflow-y-auto pt-8 pb-6 pl-6 bg-n-surface-1 dark:bg-[#101011]">
              <router-view />
            </div>
          </div>
        </woot-modal>
        <CommandBar />
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
        <CopilotContainer />
        <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
      </template>
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
    </main>
  </div>
</template>
